/* eslint-disable camelcase */
import { AnnotationResult, CriticalThinkingResult } from 'bellmere/types.d';
import { Sentence } from 'bellmere/validations/document/TextSegment.d';
import { ComponentClass, MouseEvent } from 'react';
import { ReasonCollection } from 'src/react_components/wombat/quality-assurance/interactions/mentoring-panel-types';
import { BellmereQA } from './bellmere-qa/qa-types.d';

export interface CommentInputAnnotation extends Partial<Annotation> {
  correctedSentence?: string;
  commentHelperId?: number;
  infographic?: CommentHelperInfographic;
  isCommentFlagged: boolean;
  summary?: string;
  timestamp: string;
  video?: CommentHelperVideo;
  hideCommentType?: boolean;
  commentType?: string;
}

/* eslint-disable camelcase */
export type Annotation = {
  qa_action?: string;
  category_name?: string;
  citeStatus: CiteStatus;
  cite_status?: CiteStatus;
  comment: string;
  commentCategory?: CommentCategory;
  commentFlagged?: boolean;
  commentHelperId?: number;
  commentHelperIds?: number[];
  comment_category?: string;
  comment_helper_ids?: number[];
  comment_type?: string;
  correctedSentence?: string;
  deleted: boolean;
  hide_comment_type?: boolean;
  highlights: HTMLElement[];
  highlightsText: string;
  id: number | null;
  previousComment: string;
  previousCommentCategory: CommentCategory;
  restorable: boolean;
  sources: number[];
  submissionUuid?: string
  submission_uuid?: string
  timestamp: string;
  display_type: DisplayType;
  highlights_text: string;
  comment_helpers?: CommentHelper[]
  comment_helper_id?: number
  commentHelpers?: CommentHelper[]
  bellmere: boolean
  additionalInfo?: ReturnType<typeof Annotator.getAdditionalInfo>;
  additional_info?: ReturnType<typeof Annotator.getAdditionalInfo>;
  task_aligned?: boolean;
  taskAligned?: boolean;
  paragraph_indices?: number[];
  sentences?: string[];
}

export type CommentHelperVideo = {
  id: number;
  video_url: string;
  description: string;
}

export type CommentHelperInfographic = {
  id: number;
  alt_text: string;
  description: string;
  image: {
    url: string;
  }
}

export type CitationObject = {
  sentence: string,
  sources: number[],
  match_probability: number
}

export enum CiteStatus {
  notActioned = 'not_actioned',
  cited = 'cited',
  notCited = 'not_cited'
}

export enum CommentCategory {
  All = 'all',
  TaskAndContent = 'task_and_content',
  CriticalThinking = 'critical_thinking',
  General = 'general',
  Language = 'language',
  SpellingAndGrammar = 'spelling_and_grammar',
  Structure = 'structure',
  TextMatch = 'text_match',
  UnderstandingAndComprehension = 'understanding_and_comprehension',
  UseOfSources = 'use_of_sources',
  Validate = 'validate'
}

// Icon for the category
export enum CategoryClassName {
  All = 'comment-category-all',
  TaskAndContent = 'comment-category-task-and-content',
  CriticalThinking = 'comment-category-critical-thinking',
  Language = 'comment-category-language',
  SpellingAndGrammar = 'comment-category-spelling-and-grammar',
  Structure = 'comment-category-structure',
  UseOfSources = 'comment-category-use-of-sources',
  Validate = 'comment-category-validate'
}

// Title of the category
export enum CategoryTitle {
  All = 'All comments',
  TaskAndContent = 'Task and content',
  CriticalThinking = 'Critical thinking skills',
  Language = 'Language',
  SpellingAndGrammar = 'Spelling and grammar',
  Structure = 'Structure',
  UseOfSources = 'Use of sources',
  Validate = 'Validate'
}

export type CitationSource = {
  url: string,
  type: string
}

export type AnnotatorObject = {
  routes: Record<string, string>,
  editable: boolean,
  sandbox: boolean,
  userType: UserTypes,
  tabbedFeedbackSummaryEnabled?: boolean,
  bannedWords: string[],
  containsBannedWords: ((elements: JQuery<HTMLElement>) => boolean) | null,
  bannedWordsPopoverContent: string,
  overallCommentsFlagged: boolean,
  citationMatchCutoff: number,
  loadTimerStart: Date,
  loadTimerEnd: Date,
  portFairyEnabled: boolean;
  data: {
    submissionUUID: string,
    bellmereReviewUUID: string,
    lockVersion: number,
    annotations: Annotation[],
    citations: CitationObject[],
    citationSources: CitationSource,
    applyCitations: boolean,
    minimumAnnotationsRequired: number,
    minimumNotCitedTextMatchesRequired: number,
    minimumFeedbackSummaryLength: number,
    minimumFeedbackIntroductionLength: number,
    highlights: JQuery<HTMLSpanElement>,
    commentHelpers: CommentHelper[],
    commentCategories: CommentCategory[],
    sortOrders: Record<string, number>
    client?: ClientData,
    product: Products,
    bellmereResults?: BellmereResults,
    bellmereRules?: BellmereBusinessRules,
    insertBellmereAnnotations: boolean,
    persistBellmereResults: boolean,
    wordCount: number,
    commentHelperLookup: Record<number, CommentHelper>,
    cannedReferralsTitle: string,
    richMediaEventsPath: string,
    criticalThinkingLimit: number,
    bellmereQAHeaderData: BellmereQA.HeaderData,
    bellmereQAReasons: ReasonCollection,
    viewingAsBellmereQa: boolean
  },
  bunyipV3: boolean,
  commentTypeGroups: () => { label: string, value: string, options?: CommentTypeOption[] }[],
  commentTypeOptions: () => CommentTypeOption[],
  commentCategoriesCSSMap: Map<string, string>,
  updateFeedbackHelperContents: () => void,
  highlighter: HighligherType, // TextHighlighter object
  selectHighlight: (timestamp: string, highlight?: boolean, paragraphIndex?: string | null) => void,
  deselectHighlights: () => void,
  setFilter: (filterType: string) => void | null,
  scrollTo: (timestamp: string, top?: boolean, paragraphIndex?: string | null) => void,
  showYambaButton: boolean,
  enableHighlighter: (enable?: boolean) => void,
  removeHighlight: (timestamp: string) => void,
  completeAnnotationSave: () => void,
  handleAjaxError: (xhr: JQuery.jqXHR, title: string) => void,
  clearCurrentHighlight?: () => void,
  addCommentHelperToFeedbackSummary: (commentHelper: CommentHelper) => void,
  commentHelpersForFeedbackSummary: CommentHelper[],
  checkIfReviewFinished: () => void,
  sentenceLookup: (documentRoot: HTMLElement) => Sentence[],
  sortBellmereAnnotationResults: (
    results: AnnotationResult[],
    rules: BellmereBusinessRules,
    sentenceCount: number,
    wordCount: number,
    commentHelperLookup: Record<number, CommentHelper>
  ) => AnnotationResult[]
  meetsBellmereRules: (
    result: AnnotationResult,
    appliedResults: AnnotationResult[],
    rules: BellmereBusinessRules,
    commentHelperLookup: Record<number, CommentHelper>
  ) => boolean,
  bellmereReady: boolean,
  getAdditionalInfo?: (timestamp: string | number) => Record<string, string | number>,
}

export type ClientData = {
  alt_text: string,
  logo: string,
  general_referral: GeneralReferral | null
}

export type GeneralReferral = {
  body: string,
  image: string,
  image_alt_text: string,
  trackable_link: string
}

export type YambaObject = {
  AddHighlightToCurrentDiscussion: ComponentClass<{ comment: string, annotationText: string, timestamp: string }>,
  AddHighlightToSession: ComponentClass<{ timestamp: string }>
}
/* eslint-disable camelcase */
export type CommentHelper = {
  id?: number,
  category?: CommentCategory,
  name?: string,
  comment_type?: string,
  content?: string,
  minimum_count?: number,
  summary?: string,
  sort_order?: number,
  maximum_annotations?: number,
  content_2?: string,
  content_3?: string,
  timestamp?: number,
  infographic?: CommentHelperInfographic,
  video?: CommentHelperVideo,
  hide_comment_type?: boolean
}

export type CommentTypeOption = {
  category: CommentCategory,
  maximumAnnotations: number,
  name: string,
  sortOrder: number,
  value: number,
  annotationCount?: number,
  label?: string
}

export enum UserTypes {
  Tutor = 'tutor',
  Student = 'student'
}

type HighligherType = {
  serializeHighlights: () => string,
  removeHighlights: (element?: HTMLElement) => void,
  deserializeHighlights: (json: Record<string, unknown>) => void,
  highlightHandler: () => void,
  disable: () => void,
  setColor: (colour: string) => void,
  doHighlight: (keepRange?: boolean) => void,
  el: HTMLElement,
  options: {
    color: string,
    highlightedClass: string,
    onAfterHighlight: (range: Range, highlights: HTMLElement[], timestamp: number) => void,
  },
  enable: () => void,
  getHighlights: (params: { grouped: boolean }) => {
    chunks: HTMLElement[],
    timestamp: string,
    toString: () => string
  }[]
}

export type CommentHelperSummary = {
  firstTimestamp: string,
  count: number
}

export type CommentHelpersSummary = {
  [key: number]: CommentHelperSummary
}

export interface FeedbackSummaryArea {
  category: CommentCategory,
  categoryName: string,
  commentHelpers: CommentHelper[],
  deleteCommentHelper: boolean,
  comment_helpers_feedback_summary_areas,
  comment_helpers: CommentHelper[],
  category_name: string,
  content: string
}

export type TutorData = {
  avatarUrl: string,
  name: string,
  flag: string,
  location: string,
  joined: string,
}

export type FeedbackData = {
  id: string,
  summary: string,
  areas: FeedbackAreaData[],
}

export type FeedbackAreaDataMedia = {
  id: number,
  video?: CommentHelperVideo,
  infographic?: CommentHelperInfographic
}

export type FeedbackAreaData = {
  id: string,
  area: string,
  name: string,
  text: string,
  identifier: string,
  medias?: FeedbackAreaDataMedia[]
}

export type CannedReferralData = {
  name: string,
  body: string,
  link: string,
  linkUrl: string,
  linkText: string,
}

export type CategoryTab = {
  id: string;
  count: number;
  icon: CategoryClassName;
  title: CategoryTitle;
}

export const CATEGORIES: string[] = [
  'All', 'TaskAndContent', 'CriticalThinking', 'Structure', 'Language', 'SpellingAndGrammar', 'UseOfSources'
];

export interface RichMediaEvent {
  id?: number,
  submission_annotation_id?: number,
  rich_media_id: number,
  event_type: EventType,
  created_at?: string,
  updated_at?: string
}

export enum EventType {
  Viewed = 'viewed',
  Played = 'played',
  Ended = 'ended'
}

export type CommentViewExpandedProps = {
  annotationText: string,
  bulkDeleteMode: boolean,
  commentCategory: CommentCategory,
  commentCategoryAndType: string,
  capitalizedCommentCategory: string,
  commentType: string,
  commentIcon: string,
  commentHelpersSummary: CommentHelpersSummary,
  confirmingDeletion: boolean,
  contextWidth: number,
  hasSources: boolean,
  inDiscussion: boolean,
  isEditable: boolean,
  isEditing: boolean,
  isEditingDisabled: boolean,
  isPanelSelected: boolean;
  markedForDeletion: boolean,
  restorable: boolean,
  savingSubmission: boolean,
  selectedParagraphIndex: string | null,
  annotation: CommentInputAnnotation,
  viewingAsBellmereQa: boolean,
  bellmereQAEditable: boolean,
  assignCommentVariants: (newCommentHelperId: number, callback: (newComment: string) => void) => void,
  cancelEdit: () => void,
  editComment: () => void,
  handleRestoreAnnotation: () => void,
  keyboardSelectHighlight: (e: React.KeyboardEvent) => void;
  onCancelRemovingAnnotation: () => void,
  onMarkedForDeletionChange: () => void,
  onStartRemovingAnnotation: (fromTimestamp: string) => void,
  removeAnnotation: (e?: MouseEvent, reason?: string) => void,
  removeCitation: (reason: string) => void,
  saveCiteStatus: (status: CiteStatus) => void,
  saveComment: (comment: string, commentHelperId: number) => void,
  setBellmereQAResult: (result: BellmereQA.AnnotationResult) => void,

  bellmereQAResults: BellmereQA.AnnotationResult[],
}

export type CommentViewCollapsedProps = {
  annotation: CommentInputAnnotation;
  bellmereQAResults: BellmereQA.AnnotationResult[];
  viewingAsBellmereQa?: boolean;
  commentCategory: string;
  capitalizedCommentCategory: string;
  commentIcon: string;
  commentType: string;
  isPanelSelected: boolean;
  keyboardSelectHighlight: (e: React.KeyboardEvent) => void;
}

export interface CommentInputCoreContainerProps extends Partial<CommentViewCollapsedProps & CommentViewExpandedProps> {
  showCollapsed: boolean;
  bellmereQAEditable: boolean;
}

export enum Products {
  Cardigan = 'cardigan',
  Mildura = 'mildura',
  Bellmere = 'bellmere'
}

export type BellmereResults = {
  bellmereResultsProcessedAt: string,
  annotationResults: AnnotationResult[],
  criticalThinkingResults: CriticalThinkingResult[]
}

export type BellmereBusinessRules = {
  maximum_count: number,
  paragraph_distribution_minima: number,
  paragraph_multiplier_offset: number
}

export enum DisplayType {
  // eslint-disable-next-line @typescript-eslint/no-shadow
  Sentence = 'sentence',
  Paragraph = 'paragraph',
  Document = 'document',
}

export type FeedbackCategoryFilterProps = {
  feedback: FeedbackData;
  selectedCategories?: CommentCategory[];
  multiple?: boolean;
  onFilterChange(category: CommentCategory | CommentCategory[]): void;
  className?: string;
  filterHighlightsByCategory(category: CommentCategory): void;
  annotations: Annotation[];
}

/* It will default to the current sorting method, called 'Document order'.
 * It will have a new sorting option, to be handled client side - 'Task Requirement'.
 * This will group all Task Requirement Comment Helpers above non-Task Requirement Comment Helpers,
 * which will be sorted in Document Order within their groups.
 */

export type CommentHelperSortType = 'document-order' | 'task-requirement';

export type CommentHelperSortableProps = {
  onSortTypeChange?(sortType: CommentHelperSortType): void
  className?: string
  options: { label: string; value: string; }[]
}
